import {
  ConversationThread,
  HistoryConversationSection,
} from '../types/ChatModal.types';

export const parseDate = (value: string): Date | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
};

export const isSameCalendarDay = (left: Date, right: Date): boolean =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const isYesterday = (value: Date, reference: Date): boolean => {
  const yesterday = new Date(reference);
  yesterday.setHours(0, 0, 0, 0);
  yesterday.setDate(yesterday.getDate() - 1);
  return isSameCalendarDay(value, yesterday);
};

export const formatConversationDateTag = (value: string): string => {
  const date = parseDate(value);
  if (!date) {
    return '';
  }

  const now = new Date();
  if (isSameCalendarDay(date, now)) {
    return 'Today';
  }

  if (isYesterday(date, now)) {
    return 'Yesterday';
  }

  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatMessageTimestamp = (value: string): string => {
  const date = parseDate(value);
  if (!date) {
    return '';
  }

  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
};

export const formatHistoryTimestamp = (value: string): string => {
  const date = parseDate(value);
  if (!date) {
    return '';
  }

  const now = new Date();
  if (isSameCalendarDay(date, now)) {
    return 'Today';
  }

  if (isYesterday(date, now)) {
    return 'Yesterday';
  }

  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatLastSyncedTimestamp = (value: string | null): string => {
  if (!value) {
    return '--';
  }

  const date = parseDate(value);
  if (!date) {
    return '--';
  }

  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

const formatHistorySectionDateLabel = (value: string): string => {
  const date = parseDate(value);
  if (!date) {
    return 'Unknown date';
  }

  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const buildHistoryConversationSections = (
  conversations: ConversationThread[],
): HistoryConversationSection[] =>
  conversations.reduce<HistoryConversationSection[]>(
    (sections, conversation) => {
      const label = formatHistorySectionDateLabel(conversation.updatedAt);
      const latestSection = sections[sections.length - 1];

      if (latestSection && latestSection.label === label) {
        latestSection.conversations.push(conversation);
        return sections;
      }

      sections.push({
        label,
        conversations: [conversation],
      });
      return sections;
    },
    [],
  );
